import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api, type SettingsData, type UserRow } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import type { AuthUser } from '../hooks/useAuth.js';

// ─── Shared sub-components ────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div
      className="glass rounded-2xl px-6"
      style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
    >
      <div className="py-4 border-b border-border">
        <div className="text-sm font-semibold text-text-primary">{title}</div>
        {subtitle && <div className="text-xs text-text-dimmed mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-8 py-5 border-b border-border last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-text-primary">{label}</div>
        {description && <div className="mt-0.5 text-xs text-text-dimmed">{description}</div>}
      </div>
      <div className="shrink-0 w-72">{children}</div>
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none transition-all duration-150"
      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-blue)'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    />
  );
}

// ─── UserManagementSection ────────────────────────────────────────────────────

function UserManagementSection() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
  });

  const users: UserRow[] = data?.users ?? [];

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.updateUser(id, { role }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); toast.success('Role updated'); },
    onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.updateUser(id, { isActive }),
    onSuccess: (_, vars) => { void qc.invalidateQueries({ queryKey: ['users'] }); toast.success(vars.isActive ? 'User activated' : 'User deactivated'); },
    onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const selectCls =
    'rounded-lg px-2 py-1 text-xs font-mono text-text-primary outline-none transition-all duration-150';
  const selectStyle = { background: 'var(--bg-tertiary)', border: '1px solid var(--border)' };

  return (
    <SectionCard title="User Management" subtitle="Manage roles and access for all users">
      <div className="py-4">
        {isLoading ? (
          <div className="text-sm text-text-dimmed py-4">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-text-dimmed py-4">No users found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Name</th>
                <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Email</th>
                <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Role</th>
                <th className="pb-2 text-center text-xs font-semibold uppercase tracking-wider text-text-secondary">Active</th>
                <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-border last:border-0 cursor-pointer hover:bg-bg-tertiary transition-colors duration-100"
                  onClick={() => void navigate({ to: '/settings/users/$id', params: { id: u.id } })}
                >
                  <td className="py-3 pr-4 font-semibold text-text-primary">{u.displayName}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-text-secondary">{u.email}</td>
                  <td className="py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={u.role}
                      onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value })}
                      className={selectCls}
                      style={selectStyle}
                    >
                      <option value="viewer">viewer</option>
                      <option value="operator">operator</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="py-3 pr-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => toggleActive.mutate({ id: u.id, isActive: !u.isActive })}
                      className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
                      style={{
                        background: u.isActive ? 'var(--accent-green)' : 'var(--bg-tertiary)',
                        border: '1px solid var(--border)',
                      }}
                      aria-label={u.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <span
                        className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: u.isActive ? 'translateX(18px)' : 'translateX(2px)' }}
                      />
                    </button>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono text-xs text-text-dimmed">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </SectionCard>
  );
}

// ─── JiraPersonalTokenSection ─────────────────────────────────────────────────

function JiraPersonalTokenSection({ user }: { user: AuthUser }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(user.jiraEmail ?? '');
  const [token, setToken] = useState(user.jiraPersonalToken ?? '');
  const dirty = email !== (user.jiraEmail ?? '') || token !== (user.jiraPersonalToken ?? '');

  const save = useMutation({
    mutationFn: () => api.updateMe({ jiraEmail: email || null, jiraPersonalToken: token || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth/me'] });
      toast.success('Jira settings saved');
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  return (
    <SectionCard title="Jira Integration" subtitle="Your personal Atlassian credentials — used for posting comments and replies under your name">
      <div className="py-5 space-y-4">
        <div className="rounded-xl px-3 py-2.5 text-xs" style={{ background: 'rgba(13,207,184,0.07)', border: '1px solid rgba(13,207,184,0.2)', color: 'var(--text-secondary)' }}>
          The global API token in System settings is used by the background worker for reading tickets. Your personal credentials here are used when you post comments and replies so they appear under your name in Jira.
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary)' }}>Atlassian email</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@cer.bo"
              className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none transition-all duration-150"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-blue)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary)' }}>Personal API token</div>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="••••••••••••••••"
              className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none transition-all duration-150"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-blue)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: 'var(--accent-green)' }}>
            {user.jiraEmail && user.jiraPersonalToken ? '✓ Configured' : user.jiraEmail || user.jiraPersonalToken ? '⚠ Partially configured' : ''}
          </div>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

const AI_PROVIDER_DEFAULTS: Record<string, { label: string; modelDefault: string; modelPlaceholder: string }> = {
  bedrock: {
    label: 'AWS Bedrock (default)',
    modelDefault: 'arn:aws:bedrock:us-east-1:488762301229:application-inference-profile/czrf00s53ue1',
    modelPlaceholder: 'arn:aws:bedrock:… or leave blank for default',
  },
  anthropic: {
    label: 'Anthropic (direct API)',
    modelDefault: 'claude-sonnet-4-6',
    modelPlaceholder: 'claude-sonnet-4-6 or claude-opus-4-7',
  },
  ollama: {
    label: 'Ollama (local)',
    modelDefault: 'qwen2.5-coder:14b',
    modelPlaceholder: 'qwen2.5-coder:14b',
  },
};

export function SettingsPage() {
  usePageTitle('Settings');
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
  });

  const [form, setForm] = useState<Partial<SettingsData>>({});
  const dirty = Object.keys(form).length > 0;

  const save = useMutation({
    mutationFn: () => api.saveSettings({ ...(data ?? {}), ...form } as SettingsData),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      setForm({});
      toast.success('Settings saved');
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`),
  });

  const current = { ...(data ?? {}), ...form } as Partial<SettingsData>;
  const set = (key: keyof SettingsData) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const activeProvider = current.aiProvider || 'bedrock';
  const providerInfo = AI_PROVIDER_DEFAULTS[activeProvider];

  const [tab, setTab] = useState<'account' | 'system'>('system');

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-16 shrink-0 items-center justify-between px-6 gap-4"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(var(--glass-blur))',
          WebkitBackdropFilter: 'blur(var(--glass-blur))',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <div className="flex items-center gap-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-text-secondary">Configuration</div>
            <div className="text-xl font-bold text-text-primary leading-tight mt-0.5">Settings</div>
          </div>
          <div className="flex gap-1">
            {(['system', 'account'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold capitalize tracking-wide transition-all duration-150"
                style={tab === t
                  ? { background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(13,207,184,0.35)' }
                  : { background: 'var(--glass-bg)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }}
              >
                {t === 'system' ? '⚙ System' : '◎ Account'}
              </button>
            ))}
          </div>
        </div>
        {tab === 'system' && dirty && (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </header>

      {/* ── Account tab ── */}
      {tab === 'account' && (
        <section className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Profile */}
            {user && (
              <SectionCard title="Your Profile" subtitle="Your identity and access level on this platform">
                <div className="py-5 space-y-3">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white"
                      style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                    >
                      {user.name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="text-base font-bold text-text-primary">{user.name}</div>
                      <div className="font-mono text-sm text-text-secondary">{user.email}</div>
                      <span
                        className="inline-flex mt-1 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                        style={{
                          background: user.role === 'admin' ? 'rgba(6,182,212,0.18)' : 'rgba(13,207,184,0.12)',
                          color: user.role === 'admin' ? '#06b6d4' : 'var(--accent-blue)',
                          border: `1px solid ${user.role === 'admin' ? '#06b6d433' : 'rgba(13,207,184,0.25)'}`,
                        }}
                      >
                        {user.role}
                      </span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Jira personal token — all users */}
            {user && <JiraPersonalTokenSection user={user} />}

            {/* User Management — admin only */}
            {user?.role === 'admin' && <UserManagementSection />}

            {/* Audit Log — admin only */}
            {user?.role === 'admin' && (
              <SectionCard title="Audit Log" subtitle="View all user actions and authentication events">
                <div className="py-5">
                  <Link
                    to="/settings/audit-logs"
                    className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-90 inline-block"
                    style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                  >
                    View Audit Log →
                  </Link>
                </div>
              </SectionCard>
            )}
          </div>
        </section>
      )}

      {/* ── System tab ── */}
      {tab === 'system' && (
      <section className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">

        {/* Jira Integration */}
        <SectionCard title="Jira Integration" subtitle="Global service account settings — used by the worker for all ticket reads. Personal API tokens are in Account settings.">
          <SettingRow label="Atlassian base URL" description="Your Atlassian Cloud instance URL">
            <TextInput
              value={current.jiraBaseUrl ?? ''}
              onChange={set('jiraBaseUrl')}
              placeholder="https://team-xxxxxxxx.atlassian.net"
            />
          </SettingRow>
          <SettingRow label="Global API token" description="Service account token used by the background worker for reading tickets — personal credentials are in Account settings">
            <TextInput
              value={current.jiraToken ?? ''}
              onChange={set('jiraToken')}
              placeholder="••••••••••••••••"
              type="password"
            />
          </SettingRow>
          <SettingRow label="Project key" description="All ticket queries are scoped to this project (default: IM)">
            <TextInput
              value={current.jiraProject ?? ''}
              onChange={set('jiraProject')}
              placeholder="IM"
            />
          </SettingRow>
          <SettingRow label="Max results per query" description="How many tickets to fetch at once (default: 25)">
            <TextInput
              value={current.jiraMaxResults ?? ''}
              onChange={set('jiraMaxResults')}
              placeholder="25"
            />
          </SettingRow>
        </SectionCard>

        {/* AI Provider */}
        <SectionCard title="AI Provider" subtitle="Used for recipe generation during EHR scoping">
          <SettingRow
            label="Provider"
            description={`Current default: bedrock (AWS Bedrock)`}
          >
            <select
              value={current.aiProvider ?? ''}
              onChange={(e) => set('aiProvider')(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
            >
              <option value="">— use environment default (bedrock) —</option>
              <option value="bedrock">bedrock (AWS Bedrock)</option>
              <option value="anthropic">anthropic (direct API)</option>
              <option value="ollama">ollama (local)</option>
            </select>
          </SettingRow>
          <SettingRow
            label="Model"
            description={`Model ID or ARN · Default for ${providerInfo?.label ?? 'bedrock'}: ${providerInfo?.modelDefault ?? 'arn:aws:bedrock:…'}`}
          >
            <TextInput
              value={current.aiModel ?? ''}
              onChange={set('aiModel')}
              placeholder={providerInfo?.modelPlaceholder ?? 'Leave blank to use default'}
            />
          </SettingRow>
        </SectionCard>

        {/* Ollama */}
        <SectionCard title="Ollama" subtitle="Only used when AI provider is set to ollama">
          <SettingRow label="Ollama URL" description="Base URL of the Ollama instance (default: http://localhost:11434)">
            <TextInput
              value={current.ollamaUrl ?? ''}
              onChange={set('ollamaUrl')}
              placeholder="http://localhost:11434"
            />
          </SettingRow>
          <SettingRow label="Ollama model" description={`Model to use (default: qwen2.5-coder:14b)`}>
            <TextInput
              value={current.ollamaModel ?? ''}
              onChange={set('ollamaModel')}
              placeholder="qwen2.5-coder:14b"
            />
          </SettingRow>
        </SectionCard>

        {/* AWS */}
        <SectionCard title="AWS" subtitle="Credentials for EKS build discovery — leave blank to use the instance role when deployed on AWS">
          <SettingRow label="Access key ID" description="IAM access key — not needed when running on ECS/EC2 with an instance role">
            <TextInput
              value={current.awsAccessKeyId ?? ''}
              onChange={set('awsAccessKeyId')}
              placeholder="AKIA…"
            />
          </SettingRow>
          <SettingRow label="Secret access key" description="IAM secret — stored encrypted at rest">
            <TextInput
              value={current.awsSecretAccessKey ?? ''}
              onChange={set('awsSecretAccessKey')}
              placeholder="••••••••••••••••"
              type="password"
            />
          </SettingRow>
          <SettingRow label="Session token" description="Only needed for temporary STS credentials — leave blank when using a permanent IAM access key">
            <TextInput
              value={current.awsSessionToken ?? ''}
              onChange={set('awsSessionToken')}
              placeholder="Leave blank for permanent IAM credentials"
              type="password"
            />
          </SettingRow>
          <SettingRow label="Default region" description="Primary region for EKS discovery — all regions are scanned regardless">
            <TextInput
              value={current.awsRegion ?? ''}
              onChange={set('awsRegion')}
              placeholder="us-east-1"
            />
          </SettingRow>
        </SectionCard>

        {/* Access Control — admin only */}
        {user?.role === 'admin' && (
          <SectionCard title="Access Control" subtitle="New SSO users and role permissions">
            <SettingRow
              label="Default role for new SSO users"
              description="Role assigned on first login via Microsoft SSO (default: viewer)"
            >
              <select
                value={current.defaultUserRole ?? ''}
                onChange={(e) => set('defaultUserRole')(e.target.value)}
                className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
              >
                <option value="">— viewer (default) —</option>
                <option value="viewer">viewer — read-only access</option>
                <option value="operator">operator — can create sessions and resolve conflicts</option>
                <option value="admin">admin — full access including settings</option>
              </select>
            </SettingRow>
            <SettingRow label="Role capabilities" description="What each role can do">
              <div className="space-y-2 text-xs text-text-secondary">
                <div className="flex gap-2">
                  <span className="rounded px-1.5 py-0.5 font-mono" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>viewer</span>
                  <span>Read-only — can view sessions, conflicts, KB, Jira</span>
                </div>
                <div className="flex gap-2">
                  <span className="rounded px-1.5 py-0.5 font-mono" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>operator</span>
                  <span>Can create sessions, scope EHRs, resolve conflicts, edit KB</span>
                </div>
                <div className="flex gap-2">
                  <span className="rounded px-1.5 py-0.5 font-mono" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>admin</span>
                  <span>Full access — settings, user management, all operator actions</span>
                </div>
              </div>
            </SettingRow>
          </SectionCard>
        )}

        {/* User Management — admin only */}
        {user?.role === 'admin' && <UserManagementSection />}

        {/* Audit Log — admin only */}
        {user?.role === 'admin' && (
          <SectionCard title="Audit Log" subtitle="View all user actions and authentication events">
            <div className="py-5">
              <Link
                to="/settings/audit-logs"
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-90 inline-block"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
              >
                View Audit Log →
              </Link>
            </div>
          </SectionCard>
        )}

        {/* Infrastructure */}
        <SectionCard title="Infrastructure" subtitle="Clinic definition sync from the infrastructure repo">
          <SettingRow label="GitHub token" description="Personal access token with repo read access to the infrastructure repo">
            <TextInput
              value={current.githubToken ?? ''}
              onChange={set('githubToken')}
              placeholder="ghp_..."
              type="password"
            />
          </SettingRow>
          <SettingRow label="Repo owner" description="GitHub org or user that owns the infrastructure repo (default: cerbo-medical)">
            <TextInput
              value={current.githubInfraOwner ?? ''}
              onChange={set('githubInfraOwner')}
              placeholder="MD-HQ"
            />
          </SettingRow>
          <SettingRow label="Repo name" description="Name of the infrastructure repo (default: infrastructure)">
            <TextInput
              value={current.githubInfraRepo ?? ''}
              onChange={set('githubInfraRepo')}
              placeholder="infrastructure"
            />
          </SettingRow>
          <SettingRow label="Sync interval" description="How often to sync clinic definitions (default: 24)">
            <div className="flex items-center gap-2">
              <TextInput
                value={current.infraSyncIntervalHours ?? ''}
                onChange={set('infraSyncIntervalHours')}
                placeholder="24"
                type="number"
              />
              <span className="text-xs text-text-secondary shrink-0">hours</span>
            </div>
          </SettingRow>
        </SectionCard>

        {/* Scoping */}
        <SectionCard title="Scoping" subtitle="Controls for automatic EHR recipe discovery">
          <SettingRow
            label="Auto-scope on session create"
            description="When enabled, scoping starts automatically when a session is created for an EHR with no valid recipe. Default: off."
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={current.auto_scope_on_session_create === 'true'}
                onChange={(e) => setForm((p) => ({ ...p, auto_scope_on_session_create: e.target.checked ? 'true' : 'false' }))}
                className="w-4 h-4 accent-accent-blue"
              />
              <span className="text-sm text-text-secondary">Enabled</span>
            </label>
          </SettingRow>
          <SettingRow
            label="Recipe staleness threshold"
            description="Recipes older than this many days are considered stale. Start Import is blocked until rescoping. Default: 30."
          >
            <input
              type="number"
              min={1}
              max={365}
              value={current.rescope_staleness_days || '30'}
              onChange={(e) => setForm((p) => ({ ...p, rescope_staleness_days: e.target.value }))}
              className="w-full rounded-xl px-3 py-2 text-sm font-mono text-text-primary outline-none"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
            />
          </SettingRow>
        </SectionCard>

        {/* About */}
        <SectionCard title="About">
          <div className="py-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-text-secondary">Platform</span>
              <span className="text-sm font-semibold text-text-primary">Cerbo Imports · AI Platform</span>
            </div>
            {user && (
              <>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs uppercase tracking-widest text-text-secondary">Signed in as</span>
                  <span className="font-mono text-sm text-text-secondary">{user.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-text-secondary">Role</span>
                  <span
                    className="rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
                    style={{
                      background: user.role === 'admin' ? 'var(--accent-purple)22' : 'var(--accent-blue)22',
                      color: user.role === 'admin' ? 'var(--accent-purple)' : 'var(--accent-blue)',
                      border: `1px solid ${user.role === 'admin' ? 'var(--accent-purple)' : 'var(--accent-blue)'}55`,
                    }}
                  >
                    {user.role}
                  </span>
                </div>
              </>
            )}
          </div>
        </SectionCard>
        </div>
      </section>
      )}
    </div>
  );
}
